<template>
    <b-modal
        id="module-show-modules"
        v-model="modal"
        :title="$t('modules.add_module')"
        modal-class="apprentice-default-modal"
        ok-only
        :ok-title="$t('common.close')"
        size="xl">
        <div class="modules-list">
            <div class="row">
                <div class="col-12 col-sm-4 col-md-3 sidebar">
                    <design-2021-module-show-filters-component
                        :modules-in="modulesIn"
                        :education-levels-in="educationLevels"
                        @update-filters="setFilters">
                    </design-2021-module-show-filters-component>
                </div>

                <div class="col-12 col-sm-8 col-md-9 module-list-main-content">
                    <div class="modules-grid">
                        <design-2021-module-show-tools-component
                            @update-search-term="updateSearchTerm"
                            @update-sorting="updateModuleSorting">
                        </design-2021-module-show-tools-component>

                        <div
                            class="row modules"
                            v-if="filteredModules.length >= 1">
                            <div
                                class="col-12 col-md-6 col-xl-6 module-item"
                                v-for="module in perpageModules"
                                :key="module.id">
                                <design-2021-module-card-component
                                    :class="canRegister(module) ? 'open' : 'closed'"
                                    :module-in="module"
                                    @click.native="showModuleDetailModal(module.id)">
                                    <template #header>
                                        <div class="module-show-actions">
                                            <span
                                                class="btn btn-primary-icon btn-registration-open user-register-status"
                                                v-if="canRegister(module)"
                                                :v-b-tooltip="$t('modules.registration_open')">
                                            </span>

                                            <span
                                                class="btn btn-warning-icon btn-registration-closed user-register-status"
                                                v-else
                                                :v-b-tooltip="$t('modules.registration_closed')">
                                            </span>

                                            <span
                                                class="checked btn btn-checked btn-success-icon"
                                                v-if="module.hasModuleUserByUserId($authUser.id)"
                                                :v-b-tooltip="$t('modules.linked_module')">
                                            </span>

                                            <b-button
                                                class="btn-show btn-show-module remove-icon-margin"
                                                :v-b-tooltip="$t('modules.view_module')"
                                                variant="primary-icon"
                                                @click.prevent.stop="showModuleDetailModal(module.id)">
                                            </b-button>
                                        </div>
                                    </template>
                                </design-2021-module-card-component>
                            </div>

                            <design-2021-module-show-module-modal-component
                                ref="module-show-module-detail"
                                @update-signed-user="updateSignedUser">
                            </design-2021-module-show-module-modal-component>

                            <design-2021-module-show-unregister-modal-component ref="module-show-unregister">
                            </design-2021-module-show-unregister-modal-component>

                            <div
                                class="apprentice-pagination col-12"
                                v-if="filteredModules.length > perPage">
                                <b-pagination
                                    v-model="currentPage"
                                    :total-rows="rows"
                                    :per-page="perPage">
                                </b-pagination>
                            </div>
                        </div>

                        <div
                            class="not-found with-header empty-modules-list"
                            v-else>
                            <div class="icon">
                                <i class="fas fa-exclamation-circle"></i>
                            </div>
                            <div class="alert-notice">{{ $t('modules.no_modules_found') }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </b-modal>
</template>

<script>
import { ords } from '../../../app';
import moment from 'moment';

export default {
    name: 'design-2021-module-show-modules-component',
    props: {
        modulesIn: Array,
    },
    data() {
        return {
            perPage: 6,
            currentPage: 1,
            modules: {},
            filters: {
                tags: [],
                hours: [],
                registered: [1, 2],
            },
            searchTerm: '',
            sorting: 1,
            educationLevels: null,
            modal: false,
        };
    },
    mounted() {
        this.axios.get(this.routes.educationLevels()).then(
            (response) => {
                this.educationLevels = response.data;
            },

            (error) => {
                this.alert.ajaxError(error);
            },
        );

        ords.use('module')
            .with('module_dates', (dateCallback) => {
                dateCallback.with('module_users');
                dateCallback.with('module_period', (modulePeriodsCallback) => {
                    modulePeriodsCallback.with('period');
                });
            })
            .with('file', (fileCallback) => {
                fileCallback.with('image');
            })
            .with('module_tags')
            .with('education_levels')
            .with('las_groups', (lasCallback) => {
                lasCallback.with('students');
            })
            .with('las_main_groups', (lasMainCallback) => {
                lasMainCallback.with('students');
            })
            .get()
            .subscribe((modules) => {
                this.modules = modules;
            });
    },

    computed: {
        rows() {
            return this.modules ? this.modules.length : 0;
        },

        perpageModules() {
            if (this.filteredModules.length > this.perPage) {
                const start = (this.currentPage - 1) * this.perPage;
                return this.filteredModules.length - start > this.currentPage
                    ? this.filteredModules.slice(start, this.perPage * this.currentPage)
                    : this.filteredModules.slice(start);
            }

            return this.filteredModules;
        },

        filteredModules() {
            if (!this.modules.length) {
                return [];
            }

            let modules = this.modules.filter((module) => {
                if (module.las_groups.length > 0) {
                    let hasAccess = 0;

                    for (const las of module.las_groups) {
                        for (const student of las.students) {
                            if (student.user_id === this.$authUser.id) {
                                hasAccess = 1;
                            }
                        }
                    }

                    if (hasAccess === 0) {
                        return false;
                    }
                }
                if (module.las_main_groups.length > 0) {
                    let hasAccess = 0;

                    for (const las of module.las_main_groups) {
                        for (const student of las.students) {
                            if (student.user_id === this.$authUser.id) {
                                hasAccess = 1;
                            }
                        }
                    }

                    if (hasAccess === 0) {
                        return false;
                    }
                }
                if (this.filters.tags.length) {
                    let check = false;
                    tagsLoop: for (const tag_id of this.filters.tags) {
                        for (const tag of module.module_tags) {
                            if (tag_id === tag.id) {
                                check = true;
                                break tagsLoop;
                            }
                        }
                    }

                    if (!check) {
                        return false;
                    }
                }
                if (this.filters.hours.length) {
                    let check = false;
                    hoursLoop: for (const duration of this.filters.hours) {
                        for (const date of module.module_dates) {
                            if (duration === date.module_period.duration) {
                                check = true;
                                break hoursLoop;
                            }
                        }
                    }
                    if (!check) {
                        return false;
                    }
                }

                if (this.filters.registered.length) {
                    let register = false;
                    registerLoop: for (const canRegister of this.filters.registered) {
                        for (const moduleUntilDate of module.module_dates) {
                            const today = moment().format('YYYY-MM-DD');
                            const deadline =
                                moduleUntilDate.module_period.period.sign_up_until_date !== null
                                    ? moduleUntilDate.module_period.period.sign_up_until_date
                                    : moment().add(1, 'days').format('YYYY-MM-DD');
                            const signupFrom =
                                moduleUntilDate.module_period.period.sign_up_from_date !== null
                                    ? moduleUntilDate.module_period.period.sign_up_from_date
                                    : moment().subtract(1, 'days').format('YYYY-MM-DD');
                            if (
                                (moment(today).isBefore(deadline) &&
                                    canRegister === 1 &&
                                    moment(signupFrom).isBefore(today)) ||
                                (moment(deadline).isBefore(moment(today)) &&
                                    canRegister === 0 &&
                                    moment(signupFrom).isBefore(today)) ||
                                (moment(today).isBefore(deadline) &&
                                    canRegister === 2 &&
                                    !moment(signupFrom).isBefore(today))
                            ) {
                                register = true;
                                break registerLoop;
                            }
                        }
                    }

                    if (!register) {
                        return false;
                    }
                }

                if (this.searchTerm.length >= 3) {
                    let check = false;
                    const lowerCasedTerm = this.searchTerm.toLowerCase();

                    if (module.title.toLowerCase().includes(lowerCasedTerm)) {
                        check = true;
                    }

                    if (module.description.toLowerCase().includes(lowerCasedTerm)) {
                        check = true;
                    }

                    for (const tag of module.module_tags) {
                        if (tag.name.toLowerCase().includes(lowerCasedTerm)) {
                            check = true;
                            break;
                        }
                    }

                    if (!check) {
                        return false;
                    }
                }

                return true;
            });

            if (this.sorting === 1) {
                modules.sort((a, b) => a.title.localeCompare(b.title));
            }

            if (this.sorting === 2) {
                modules.sort((a, b) => b.created_at.localeCompare(a.created_at));
            }

            if (this.sorting === 3) {
                const sortedModules = modules.map((module) => {
                    let daysUntil = 0;
                    for (const date of module.module_dates) {
                        const toDay = moment().format('YYYY-MM-DD');
                        const signupFrom =
                            date.sign_up_from_date !== null
                                ? date.sign_up_from_date
                                : moment().subtract(1, 'days').format('YYYY-MM-DD');
                        let currentdays = 0;
                        if (moment(signupFrom).isAfter(toDay)) {
                            currentdays = 888;
                        } else {
                            currentdays = date.sign_up_until_date
                                ? moment(date.sign_up_until_date).diff(moment().format('YYYY-MM-DD'), 'days')
                                : 999;
                            currentdays = currentdays <= 0 ? 999 : currentdays;
                        }

                        if (daysUntil === 0) {
                            daysUntil = currentdays;
                        } else {
                            daysUntil = currentdays < daysUntil ? currentdays : daysUntil;
                        }
                    }

                    return {
                        id: module.id,
                        days_until: daysUntil !== 0 ? daysUntil : 999,
                    };
                });

                sortedModules.sort((a, b) => {
                    if (a.days_until > b.days_until) return 1;
                    if (a.days_until < b.days_until) return -1;
                    return 0;
                });

                modules = sortedModules.map((sorted_module) => {
                    const pos = modules.map((module) => module.id).indexOf(sorted_module.id);
                    return modules[pos];
                });
            }

            return modules;
        },
    },

    methods: {
        showModuleDetailModal(moduleId) {
            this.$refs['module-show-module-detail'].show(moduleId);
        },

        showModuleUnregisterModal(module) {
            if (module.userHasMultipleRegistrations(this.$authUser.id)) {
                this.$refs['module-show-unregister'].show(module.id);
            } else {
                this.alert
                    .confirm(
                        this.$t('modules.unsubscribe_module'),
                        this.$t('modules.are_you_sure_you_want_to_unsubscribe_from_this_module'),
                        this.$t('modules.yes_sign_out'),
                    )
                    .then((confirm) => {
                        if (!confirm) {
                            return;
                        }

                        let moduleUser = module.findModuleUserByUserId(this.$authUser.id);
                        this.alert.saving();
                        this.axios.delete(this.routes.deleteModuleUser(moduleUser.id)).then(
                            (response) => {
                                moduleUser.remove();
                                this.alert.success();
                            },
                            (error) => {
                                this.alert.ajaxError(error);
                            },
                        );
                    });
            }
        },

        setFilters(filters) {
            this.filters = { ...filters };
        },

        updateSearchTerm(term) {
            this.searchTerm = term;
        },

        updateModuleSorting(value) {
            this.sorting = value;
        },

        show() {
            this.modal = true;
        },

        canRegister(module) {
            let today = moment(new Date()).format('YYYY-MM-DD');
            let closed = false;
            let open = false;
            for (const date of module.module_dates) {
                const deadline = date.module_period.period.sign_up_until_date
                    ? date.module_period.period.sign_up_until_date
                    : moment().add(1, 'days').format('YYYY-MM-DD');
                if (moment(today).isBefore(deadline)) {
                    open = true;
                }
            }

            return open || (open && closed);
        },

        updateSignedUser(user) {
            this.$emit('update-signed-user', user);
        },
    },
};
</script>

<style scoped lang="scss">
.sidebar {
    margin-bottom: $grid-gutter-width;
}

.modules {
    .module-item {
        margin-bottom: map-get($grid-gutter-spacing, 'fifteen');

        .module-card {
            &.closed {
                filter: grayscale(1);
            }
        }
    }
}

.module-show-actions {
    display: flex;
    flex-direction: row;
    align-items: center;

    > .btn {
        &:not(:last-child) {
            margin-right: map-get($grid-gutter-spacing, 'eight');
        }

        &:before {
            margin-right: 0;
        }
    }
}

.not-found {
    margin-top: #{3 * map-get($grid-gutter-spacing, 'twenty-one')};
}
</style>
